/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { API, CONFIGUREROUTES, DIALOGOPENFOR } from "../../../globals/globals";
import { lteCellsColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { deviceVersionSelector } from "../../../store/filters/Nodes/selectors";
import { confirmDialogSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import {
  useGetLteCellsQuery,
  useGetRadioNodesQuery,
  useGetZoneCellsQuery,
  useSetDeviceConfigMutation,
} from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setConfigCounts } from "../../../store/api/deviceConfig/reducers";
import {selectedDeviceSelector, isOpenRnDrawerSelector, isCellSavedSelector } from "../../../store/api/devices/selectors";
import React from "react";
import { ILteCell, ILteCellDataGridRow } from "../../../store/api/deviceConfig/types";
import { OPERATIONTYPES, constStr } from "../../../globals/constants";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { prepareLteCellPayload } from "../../../components/Configuration/helpers";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useLteCellsData = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");

  const [status, setStatus] = useState("");
  const [alarm, setAlarm] = useState("");
  const swVersion = useSelector(deviceVersionSelector);
  const { lteCell = initialState.pagination } = useSelector(paginationModelSelector);

  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [setDeviceConfig] = useSetDeviceConfigMutation();
  const [selectedLteCellRow, setSelectedLteCellRow] = React.useState<ILteCellDataGridRow>();
  const [selectedLteCellDetails, setSelectedLteCellDetails] = React.useState<ILteCell | null>(null);
  const [rowClicked, setRowClicked] = useState(false);

  const args = {
    filters: `page=${lteCell.page.toString()}&size=${lteCell.pageSize.toString()}&status=${status.toString()}&alarm=${alarm.toString()}&swVersion=${swVersion}`,
    deviceKey: locationPath[2],
  };

  const { data, isLoading, refetch } = useGetLteCellsQuery(args, {
    pollingInterval: API.ENDPOINTS.LTECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const zoneArgs = {
    filters: `page=${"0"}&size=${"100"}`,
    deviceKey: locationPath[2],
    deviceNode: selectedDevice?.nodeType,
  };
  const { data: zoneList, refetch :zonerefetch } = useGetZoneCellsQuery(zoneArgs, {
    pollingInterval: API.ENDPOINTS.LTEZONECELLS.pollingInterval || API.POLLING_INTERVAL,
  });

  const rnArgs = {
    filters: `page=${"0"}&size=${"100"}`,
    deviceKey: locationPath[2],
  };

  const { data: radioNodeList } = useGetRadioNodesQuery(rnArgs, {
    pollingInterval: API.ENDPOINTS.RADIONODES.pollingInterval || API.POLLING_INTERVAL,
  });
  const isOpenRnDrawer = useSelector(isOpenRnDrawerSelector);
  const [zonesData, setZonesData] = useState(zoneList);
  const [cellsData, setCellsData] = useState(data);
  const isCellSaved = useSelector(isCellSavedSelector);
  

  const deleteLteCellHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.LTECELL) {
          const lteCellPayload =
            !!selectedDevice?.objectKey && prepareLteCellPayload(selectedLteCellRow, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(lteCellPayload);
          messageStr = `The deletion process for LTE Cell has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  useEffect(() => {
    refetch();
  }, [status, alarm, refetch]);

  useEffect(() => {
    if (!isOpenRnDrawer && isCellSaved) {
      zonerefetch().then(({ data }) => {
        setZonesData(data);
      });

      refetch().then(({ data }) => {
        setCellsData(data);
      });
    }
  }, [isOpenRnDrawer,zonesData, cellsData]);

  useEffect(() => {
    if (!!data?.data) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.LTECELL.Path]: data?.totalItems || 0 }));
    }
  }, [data?.data, data?.totalItems, dispatch]);

  return {
    permissions,
    data,
    isLoading,
    lteCellsColumns,
    setStatus,
    setAlarm,
    deleteLteCellHandler,
    setSelectedLteCellRow,
    zoneList,
    selectedLteCellDetails,
    setSelectedLteCellDetails,
    rowClicked,
    setRowClicked,
    radioNodeList,
    locationPath,
    cellsData,
    zonesData
  };
};
